import React, { useEffect, useState, useCallback } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import ClientMatterPicker from '../../../components/shared/ClientMatterPicker';
import type { ClientMatterSelection } from '../../../components/shared/ClientMatterPicker';

const BillDrawer: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const store = useBillingStore();
  const { createManualBill, updateBill, fetchThemes } = useBilling();
  const themes = useBillingStore((s) => s.themes);
  const isEdit = store.drawerMode === 'edit';
  const editBill = isEdit
    ? store.bills.find((b) => b.bill_id === store.selectedBillId) ?? null
    : null;

  const [matterId, setMatterId] = useState('');
  const [matterLabel, setMatterLabel] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [description, setDescription] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [themeId, setThemeId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editBill) {
      setMatterId(editBill.matter_id);
      setMatterLabel(editBill.matter?.matter_number || editBill.matter_id);
      setClientLabel(editBill.matter?.client?.client_name || '');
      setShowPicker(false);
      setDescription(editBill.description || '');
      setPeriodStart(editBill.period_start?.split('T')[0] || '');
      setPeriodEnd(editBill.period_end?.split('T')[0] || '');
      setThemeId(editBill.theme_id || '');
    } else {
      setMatterId('');
      setMatterLabel('');
      setClientLabel('');
      setShowPicker(false);
      setDescription('');
      setPeriodStart('');
      setPeriodEnd('');
      setThemeId('');
    }
  }, [editBill, store.drawerOpen]);

  // Load themes when drawer opens
  useEffect(() => {
    if (store.drawerOpen && themes.length === 0) {
      fetchThemes().catch(() => {});
    }
  }, [store.drawerOpen]);

  const handleMatterSelect = useCallback((selection: ClientMatterSelection) => {
    setMatterId(selection.matterId);
    setMatterLabel(
      selection.matterNumber
        ? `${selection.matterNumber}${selection.matterName ? ` — ${selection.matterName}` : ''}`
        : selection.matterName || selection.matterId
    );
    setClientLabel(selection.clientName);
    setShowPicker(false);
  }, []);

  const handlePickerCancel = useCallback(() => {
    setShowPicker(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEdit && editBill) {
        await updateBill(editBill.bill_id, {
          description: description || undefined,
          period_start: periodStart || undefined,
          period_end: periodEnd || undefined,
          theme_id: themeId || undefined,
        });
        store.closeDrawer();
      } else {
        const bill = await createManualBill({
          matter_id: matterId,
          description: description || undefined,
          period_start: periodStart || undefined,
          period_end: periodEnd || undefined,
          theme_id: themeId || undefined,
        });
        store.closeDrawer();
        if (bill?.bill_id) {
          navigate(`/billing/invoices/${bill.bill_id}`);
        }
      }
    } catch {
      // error already set in store by hook
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={store.closeDrawer}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-[420px] bg-white dark:bg-gray-900 shadow-xl z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Invoice' : 'Custom Invoice'}
          </h2>
          <button
            onClick={store.closeDrawer}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Info banner */}
          {!isEdit && (
            <div className="bg-lex-accent-soft border border-lex-accent rounded-lg px-4 py-3">
              <p className="text-sm text-lex-accent">
                Creates an empty draft invoice. You can add line items manually on the detail page.
              </p>
            </div>
          )}

          {/* Client / Matter Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Client / Matter
            </label>
            {isEdit ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm">
                <div className="flex-1 min-w-0">
                  {clientLabel && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{clientLabel}</div>
                  )}
                  <div className="text-gray-900 dark:text-white truncate">{matterLabel}</div>
                </div>
              </div>
            ) : matterId && !showPicker ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm">
                <div className="flex-1 min-w-0">
                  {clientLabel && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{clientLabel}</div>
                  )}
                  <div className="text-gray-900 dark:text-white truncate">{matterLabel}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  className="p-1 text-gray-400 hover:text-lex-accent rounded flex-shrink-0"
                  title="Change client/matter"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
              </div>
            ) : showPicker ? (
              <ClientMatterPicker
                onSelect={handleMatterSelect}
                onCancel={handlePickerCancel}
                variant="light"
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="w-full px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-lex-accent hover:text-lex-accent transition-colors text-left"
              >
                Select client & matter...
              </button>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Invoice description or notes..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none"
            />
          </div>

          {/* Theme selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Invoice Theme
            </label>
            <select
              value={themeId}
              onChange={(e) => setThemeId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="">Default Theme</option>
              {themes.map((t) => (
                <option key={t.theme_id} value={t.theme_id}>
                  {t.name}{t.is_default ? ' (Default)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Period range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Period Start
              </label>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Period End
              </label>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={store.closeDrawer}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit as any}
            disabled={submitting || (!isEdit && !matterId)}
            className="flex-1 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create & Add Items'}
          </button>
        </div>
      </div>
    </>
  );
};

export default BillDrawer;
